// Lightweight client-side analytics tracker — no cookies, no third-party.
//
// Sessions:
//   - `session_id` is an opaque string stored in sessionStorage (not localStorage)
//     so it dies naturally when the tab closes.
//   - A 30-min sliding idle timer rotates the id, mimicking a "session" boundary.
//
// Public API:
//   - track(event_name, meta?)   — fire any allowed event
//   - trackPageview(path?)       — convenience, called by the App router
//   - hydrateOnRouteChange(path) — called from a Router listener

import { api } from "@/lib/api";

const SESSION_KEY = "ts-analytics-session";
const IDLE_MS = 30 * 60 * 1000; // 30 min

function newSessionId() {
  const arr = new Uint8Array(9);
  (window.crypto || window.msCrypto).getRandomValues(arr);
  return btoa(String.fromCharCode(...arr))
    .replace(/[^A-Za-z0-9]/g, "")
    .slice(0, 12) + Date.now().toString(36).slice(-4);
}

function getSession() {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    const now = Date.now();
    if (raw) {
      const obj = JSON.parse(raw);
      if (obj && obj.id && obj.lastSeen && now - obj.lastSeen < IDLE_MS) {
        obj.lastSeen = now;
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(obj));
        return obj.id;
      }
    }
    const id = newSessionId();
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ id, lastSeen: now }));
    return id;
  } catch {
    return "no-storage-" + Date.now().toString(36);
  }
}

// Ignore /admin traffic so the owner doesn't inflate their own numbers.
function shouldSkip(path) {
  if (typeof window === "undefined") return true;
  const p = path || window.location.pathname;
  return p.startsWith("/admin");
}

export function track(event_name, meta = {}) {
  if (shouldSkip()) return;
  const sid = getSession();
  if (!sid) return;
  // Fire-and-forget. Never throw into the calling flow.
  const payload = {
    session_id: sid,
    event_name,
    path: window.location.pathname,
    referrer: document.referrer || "",
    meta,
  };
  api.post("/analytics/track", payload).catch(() => {});
}

export function trackPageview(path) {
  if (shouldSkip(path)) return;
  track("pageview", { path: path || window.location.pathname });
}

/** Public helper — returns the analytics session id (creates one if missing).
 * Reused by the reaction system so a browser tab de-dupes votes without
 * needing a full auth system. */
export function getSessionId() {
  return getSession();
}
